/**
 * JerryDriveAccessLock — wspólny komponent do:
 * (a) wprowadzenia kodu PIN / gestu na ekranie logowania (mode="enter")
 * (b) wyboru i zapisania nowego kodu w ustawieniach modułu (mode="setup")
 *
 * Props:
 *  - mode: 'enter' | 'setup'
 *  - allowedType: 'pin' | 'gesture' (tylko w mode="enter" — typ wymuszony przez serwer)
 *  - onSuccess(value, type)
 *  - onCancel?: () => void
 *  - title?: string
 *  - errorMsg?: string  (np. "Niepoprawny kod")
 */
import React, { useState, useRef, useEffect, useMemo } from 'react';

const accent = '#06b6d4';
const danger = '#ef4444';
const good = '#10b981';

// === Pattern lock 3x3 (gest) ===
const GestureLock = ({ value, onChange, disabled }) => {
  const [drawing, setDrawing] = useState(false);
  const containerRef = useRef(null);
  const [dotRects, setDotRects] = useState([]);
  const sequence = value || [];

  const refreshRects = () => {
    if (!containerRef.current) return;
    const cont = containerRef.current.getBoundingClientRect();
    const rects = [];
    const dots = containerRef.current.querySelectorAll('[data-dot]');
    dots.forEach((el) => {
      const r = el.getBoundingClientRect();
      rects.push({
        id: parseInt(el.dataset.dot, 10),
        cx: r.left - cont.left + r.width / 2,
        cy: r.top - cont.top + r.height / 2,
        r: r.width / 2,
      });
    });
    setDotRects(rects);
  };

  useEffect(() => {
    refreshRects();
    window.addEventListener('resize', refreshRects);
    return () => window.removeEventListener('resize', refreshRects);
  }, []);

  const ptFromEvent = (e) => {
    const cont = containerRef.current.getBoundingClientRect();
    const t = e.touches?.[0] || e.changedTouches?.[0] || e;
    return { x: t.clientX - cont.left, y: t.clientY - cont.top };
  };

  const dotAt = (x, y) => dotRects.find((d) => Math.hypot(d.cx - x, d.cy - y) < d.r * 0.85);

  const start = (e) => {
    if (disabled) return;
    e.preventDefault();
    refreshRects();
    setDrawing(true);
    onChange([]);
    const p = ptFromEvent(e);
    const d = dotAt(p.x, p.y);
    if (d) onChange([d.id]);
  };
  const move = (e) => {
    if (!drawing || disabled) return;
    e.preventDefault();
    const p = ptFromEvent(e);
    const d = dotAt(p.x, p.y);
    if (d && !sequence.includes(d.id)) {
      onChange([...sequence, d.id]);
    }
  };
  const end = () => setDrawing(false);

  // Build SVG polyline from current sequence
  const polyline = useMemo(() => {
    if (sequence.length < 2 || dotRects.length === 0) return '';
    return sequence
      .map((id) => dotRects.find((d) => d.id === id))
      .filter(Boolean)
      .map((d) => `${d.cx},${d.cy}`)
      .join(' ');
  }, [sequence, dotRects]);

  return (
    <div
      ref={containerRef}
      onMouseDown={start}
      onMouseMove={move}
      onMouseUp={end}
      onMouseLeave={end}
      onTouchStart={start}
      onTouchMove={move}
      onTouchEnd={end}
      data-testid="jd-gesture-lock"
      style={{
        position: 'relative',
        width: 260, height: 260, margin: '0 auto',
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        gridTemplateRows: 'repeat(3, 1fr)',
        gap: 28,
        touchAction: 'none', userSelect: 'none',
        opacity: disabled ? 0.5 : 1,
      }}
    >
      {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((id) => {
        const active = sequence.includes(id);
        return (
          <div
            key={id}
            data-dot={id}
            data-testid={`jd-gesture-dot-${id}`}
            style={{
              width: 44, height: 44, borderRadius: '50%',
              background: active ? accent : 'rgba(255,255,255,0.07)',
              border: `2px solid ${active ? accent : 'rgba(255,255,255,0.2)'}`,
              boxShadow: active ? `0 0 16px ${accent}88` : 'none',
              transition: 'all 0.15s ease',
              margin: 'auto',
            }}
          />
        );
      })}
      <svg style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }} width="100%" height="100%">
        {polyline && (
          <polyline
            points={polyline}
            fill="none"
            stroke={accent}
            strokeWidth="3"
            strokeLinecap="round"
            strokeLinejoin="round"
            opacity="0.8"
          />
        )}
      </svg>
    </div>
  );
};

// === PIN input — 4-8 cyfr ===
const PinInput = ({ value, onChange, disabled, autoFocus }) => {
  const inputRef = useRef(null);
  useEffect(() => { if (autoFocus) inputRef.current?.focus(); }, [autoFocus]);
  return (
    <div style={{ display: 'flex', justifyContent: 'center', gap: 8, margin: '24px 0' }}>
      <input
        ref={inputRef}
        type="password"
        inputMode="numeric"
        pattern="[0-9]*"
        autoComplete="off"
        disabled={disabled}
        value={value || ''}
        onChange={(e) => onChange((e.target.value || '').replace(/\D/g, '').slice(0, 8))}
        data-testid="jd-pin-input"
        placeholder="• • • •"
        style={{
          width: '220px', padding: '14px 18px',
          fontSize: 28, letterSpacing: '0.5em', textAlign: 'center',
          borderRadius: 12,
          background: 'rgba(255,255,255,0.05)',
          border: `1px solid rgba(255,255,255,0.15)`,
          color: '#fff',
          outline: 'none',
        }}
      />
    </div>
  );
};

const JerryDriveAccessLock = ({
  mode = 'enter',
  allowedType = 'pin',
  onSuccess,
  onCancel,
  title,
  errorMsg,
}) => {
  // mode="setup": użytkownik wybiera typ; mode="enter": typ wymuszony przez backend
  const [type, setType] = useState(mode === 'setup' ? 'pin' : allowedType);
  useEffect(() => { if (mode === 'enter') setType(allowedType); }, [mode, allowedType]);

  const [pin, setPin] = useState('');
  const [pinConfirm, setPinConfirm] = useState('');
  const [gesture, setGesture] = useState([]);
  const [gestureConfirm, setGestureConfirm] = useState([]);
  const [step, setStep] = useState(1); // 1 = wprowadź, 2 = potwierdź (tylko setup)
  const [localError, setLocalError] = useState('');

  const reset = () => { setPin(''); setPinConfirm(''); setGesture([]); setGestureConfirm([]); setStep(1); setLocalError(''); };

  const handleSubmit = () => {
    setLocalError('');
    if (mode === 'enter') {
      // Wystarczy wysłać do parenta — walidacja po stronie serwera
      if (type === 'pin') {
        if (pin.length < 4) return setLocalError('PIN musi mieć min. 4 cyfry');
        onSuccess?.(pin, 'pin');
      } else {
        if (gesture.length < 4) return setLocalError('Gest musi mieć min. 4 punkty');
        onSuccess?.(gesture.join(','), 'gesture');
      }
      return;
    }
    // SETUP: 2 kroki
    if (type === 'pin') {
      if (step === 1) {
        if (pin.length < 4) return setLocalError('PIN musi mieć min. 4 cyfry');
        setStep(2);
        return;
      }
      if (pin !== pinConfirm) return setLocalError('PIN-y się różnią');
      onSuccess?.(pin, 'pin');
      return;
    }
    // gesture
    if (step === 1) {
      if (gesture.length < 4) return setLocalError('Gest musi mieć min. 4 punkty');
      setStep(2);
      return;
    }
    if (gesture.join(',') !== gestureConfirm.join(',')) return setLocalError('Gesty się różnią');
    onSuccess?.(gesture.join(','), 'gesture');
  };

  const showError = errorMsg || localError;

  return (
    <div data-testid="jd-access-lock" style={{ color: '#fff', maxWidth: 360, margin: '0 auto', textAlign: 'center' }}>
      {title && (
        <h3 style={{ margin: '0 0 16px', fontSize: 20, fontWeight: 800 }}>{title}</h3>
      )}

      {/* Wybór typu — tylko w setup */}
      {mode === 'setup' && (
        <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginBottom: 16 }}>
          {['pin', 'gesture'].map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => { setType(t); reset(); }}
              data-testid={`jd-access-type-${t}`}
              style={{
                padding: '8px 16px', borderRadius: 999, fontSize: 13, fontWeight: 700,
                background: type === t ? accent : 'rgba(255,255,255,0.05)',
                color: type === t ? '#000' : '#fff',
                border: `1px solid ${type === t ? accent : 'rgba(255,255,255,0.15)'}`,
                cursor: 'pointer',
              }}
            >
              {t === 'pin' ? '🔢 PIN' : '✋ Gest'}
            </button>
          ))}
        </div>
      )}

      <div style={{ fontSize: 13, color: '#94a3b8', marginBottom: 12, minHeight: 18 }}>
        {mode === 'enter'
          ? (type === 'pin' ? 'Podaj PIN aby otworzyć Jerry Drive' : 'Narysuj gest aby otworzyć Jerry Drive')
          : (step === 1 ? `Krok 1: ustaw ${type === 'pin' ? 'nowy PIN' : 'gest'}` : `Krok 2: powtórz ${type === 'pin' ? 'PIN' : 'gest'} dla potwierdzenia`)
        }
      </div>

      {type === 'pin' ? (
        <PinInput
          value={mode === 'enter' || step === 1 ? pin : pinConfirm}
          onChange={mode === 'enter' || step === 1 ? setPin : setPinConfirm}
          autoFocus
        />
      ) : (
        <GestureLock
          value={mode === 'enter' || step === 1 ? gesture : gestureConfirm}
          onChange={mode === 'enter' || step === 1 ? setGesture : setGestureConfirm}
        />
      )}

      {showError && (
        <div style={{ color: danger, fontSize: 13, marginTop: 8, fontWeight: 600 }} data-testid="jd-access-error">
          ⚠️ {showError}
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, marginTop: 18, justifyContent: 'center' }}>
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            data-testid="jd-access-cancel"
            style={{
              padding: '12px 20px', borderRadius: 10, fontSize: 13, fontWeight: 700,
              background: 'rgba(255,255,255,0.05)', color: '#fff',
              border: '1px solid rgba(255,255,255,0.15)', cursor: 'pointer',
            }}>Anuluj</button>
        )}
        <button
          type="button"
          onClick={handleSubmit}
          data-testid="jd-access-submit"
          style={{
            padding: '12px 22px', borderRadius: 10, fontSize: 13, fontWeight: 800,
            background: `linear-gradient(135deg, ${accent}, ${good})`,
            color: '#fff', border: 'none', cursor: 'pointer',
            boxShadow: `0 8px 20px ${accent}55`,
          }}>
          {mode === 'enter' ? '🔓 Odblokuj' : (step === 1 ? 'Dalej →' : '💾 Zapisz')}
        </button>
      </div>
    </div>
  );
};

export default JerryDriveAccessLock;
